import { EXPERIMENT_STEPS, deltaE2000, describeDeltaE, labToSrgb } from '@dyeatlas/shared'
import { useQueries } from '@tanstack/react-query'
import { ArrowLeft, BookMarked, FlaskConical, Plus, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RecordPicker } from './RecordPicker'
import { RecordDetailModal } from '../database/RecordDetailModal'
import { hasValue, labOf, resolve, type DetailRecord } from '../database/recordFields'
import { STEP_FIELDS, type FieldDef } from '../database/wizard/fields'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, EmptyState, LoadingPanel } from '@/components/ui/misc'
import { MAX_COMPARED, useComparisonList } from '@/hooks/useComparisonList'
import { api } from '@/lib/api'
import { cn, emptyDash, formatDate } from '@/lib/utils'

const MAX_RECORDS = MAX_COMPARED
const MIN_RECORDS = 2

type Tab = 'overview' | 'colour' | 'details'

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'colour', label: 'Colour' },
  { key: 'details', label: 'All fields' },
]

/**
 * Side-by-side comparison of 2–5 records. The selection is mirrored into the URL
 * so a comparison can be bookmarked and sent to a colleague.
 */
export function Compare() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [picking, setPicking] = useState(false)
  const [openRecord, setOpenRecord] = useState<string | null>(null)

  const comparison = useComparisonList()
  const fromUrl = params.get('records')

  // A shared link wins; otherwise the page shows the saved comparison list.
  const ids = useMemo(
    () =>
      (fromUrl === null ? comparison.ids : fromUrl.split(',').filter(Boolean)).slice(0, MAX_RECORDS),
    [fromUrl, comparison.ids],
  )

  const setIds = (next: string[]) => {
    comparison.set(next)
    setParams(next.length ? { records: next.join(',') } : {}, { replace: true })
  }

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['browse', 'record', id],
      queryFn: () => api.get<{ record: DetailRecord }>(`/browse/${id}`),
    })),
  })

  const records = queries
    .map((query) => query.data?.record)
    .filter((record): record is DetailRecord => Boolean(record))

  const loading = queries.some((query) => query.isLoading)

  if (ids.length === 0) {
    return (
      <EmptyState
        title="Nothing to compare yet"
        description="Pick two or more records in the Public Database, then choose Compare."
        action={
          <Button variant="secondary" onClick={() => navigate('/database')}>
            Browse the database
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <RecordPicker
        open={picking}
        onOpenChange={setPicking}
        alreadyChosen={ids}
        remainingSlots={MAX_RECORDS - ids.length}
        onConfirm={(added) => setIds([...ids, ...added].slice(0, MAX_RECORDS))}
      />

      <RecordDetailModal
        recordId={openRecord}
        onOpenChange={(open) => !open && setOpenRecord(null)}
        onOpenRecord={setOpenRecord}
      />

      <header className="flex items-start gap-3 pb-5">
        <button
          type="button"
          onClick={() => navigate('/database')}
          aria-label="Back to the database"
          className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-full border border-line text-ink-muted hover:bg-paper-muted hover:text-ink"
        >
          <ArrowLeft className="size-4" />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="font-display text-[26px] font-semibold leading-tight text-ink">
            Compare {ids.length} records
          </h1>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            Columns keep the order you chose. Differences in each row are highlighted.
          </p>
        </div>

        {ids.length < MAX_RECORDS && (
          <Button variant="secondary" size="sm" onClick={() => setPicking(true)}>
            <Plus className="size-4" />
            Add record
          </Button>
        )}
      </header>

      {ids.length < MIN_RECORDS && (
        <Card className="mb-5 px-4 py-3 text-[13px] text-ink-soft">
          A comparison needs at least two records. Add one more to see the differences.
        </Card>
      )}

      <nav className="flex gap-1.5 pb-4">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            className={cn(
              'rounded-[var(--radius-control)] border px-3 py-1.5 text-[13px] font-medium transition-colors',
              tab === entry.key
                ? 'border-brand-line bg-brand-soft text-brand'
                : 'border-line bg-paper text-ink-soft hover:bg-paper-muted',
            )}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <LoadingPanel label="Loading records" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-sm">
            <colgroup>
              <col className="w-52" />
              {records.map((record) => (
                <col key={record.id} />
              ))}
            </colgroup>

            {/* The field list runs well past a screen; without this you lose
                track of which column is which halfway down. */}
            <thead className="sticky top-0 z-10 bg-paper">
              <tr className="border-b border-line align-top">
                <th className="px-4 py-3 text-left text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
                  Record
                </th>
                {records.map((record) => (
                  <th key={record.id} className="border-l border-line px-4 py-3 text-left">
                    <ColumnHeader
                      record={record}
                      onRemove={() => setIds(ids.filter((id) => id !== record.id))}
                      onOpen={() => setOpenRecord(record.id)}
                    />
                  </th>
                ))}
              </tr>
            </thead>

            {tab === 'overview' && <OverviewBody records={records} />}
            {tab === 'colour' && <ColourBody records={records} />}
            {tab === 'details' && <DetailsBody records={records} />}
          </table>
        </Card>
      )}
    </div>
  )
}

function ColumnHeader({
  record,
  onRemove,
  onOpen,
}: {
  record: DetailRecord
  onRemove: () => void
  onOpen: () => void
}) {
  return (
    <div className="flex items-start gap-2">
      <span
        className="size-12 shrink-0 rounded-[6px] border border-line"
        style={{ backgroundColor: record.swatchHex ?? 'var(--color-paper-muted)' }}
      />

      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
          {record.kind === 'reference' ? (
            <BookMarked className="size-3" />
          ) : (
            <FlaskConical className="size-3" />
          )}
          {record.code}
        </span>
        <button
          type="button"
          onClick={onOpen}
          // Five columns leave little room, so the full title stays reachable.
          title={record.title}
          className="block w-full truncate text-left font-display text-[15px] font-semibold text-ink hover:text-brand"
        >
          {record.title}
        </button>
        <span className="block truncate text-[12px] font-normal text-ink-muted">
          {emptyDash(record.researcherName)}
        </span>
      </div>

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${record.code} from the comparison`}
        className="shrink-0 rounded-md p-1 text-ink-faint hover:bg-paper-muted hover:text-ink"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

function OverviewBody({ records }: { records: DetailRecord[] }) {
  const rows: { label: string; read: (record: DetailRecord) => string | null }[] = [
    { label: 'Type', read: (r) => (r.kind === 'reference' ? 'Reference' : 'Experiment') },
    { label: 'Dye source', read: (r) => r.dyeSource },
    { label: 'Scientific name', read: (r) => r.dyeSourceScientific },
    { label: 'Fiber', read: (r) => r.vocabulary[r.texFiberId as string] ?? null },
    { label: 'Mordants', read: (r) => r.mordants.map((m) => m.name).join(', ') || null },
    { label: 'Manuscript', read: (r) => r.manuscript },
    { label: 'Recipe number', read: (r) => (r.recipeNumber as string) ?? null },
    {
      label: 'Date',
      read: (r) =>
        r.experimentDate
          ? formatDate(r.experimentDate)
          : r.referenceYear
            ? String(r.referenceYear)
            : null,
    },
    { label: 'Place', read: (r) => r.placeOfExperiment },
    {
      label: 'Institution',
      read: (r) => r.vocabulary[r.institutionId as string] ?? null,
    },
    { label: 'Published', read: (r) => (r.publishedAt ? formatDate(r.publishedAt) : null) },
  ]

  return (
    <tbody>
      {rows.map((row) => (
        <ComparisonRow
          key={row.label}
          label={row.label}
          values={records.map((record) => row.read(record))}
        />
      ))}
    </tbody>
  )
}

/**
 * Colour is the reason most comparisons happen, so each column is measured
 * against the leftmost record rather than being left to the eye.
 */
function ColourBody({ records }: { records: DetailRecord[] }) {
  const labs = records.map(labOf)
  const reference = labs[0]

  return (
    <tbody>
      <tr className="border-b border-line">
        <th className="px-4 py-3 text-left text-[13px] font-medium text-ink-muted">Swatch</th>
        {records.map((record, index) => {
          const lab = labs[index]
          const srgb = lab ? labToSrgb(lab) : null
          return (
            <td key={record.id} className="border-l border-line px-4 py-3">
              {srgb ? (
                <div className="flex items-center gap-3">
                  <span
                    className="size-14 shrink-0 rounded-[6px] border border-line"
                    style={{ backgroundColor: srgb.hex }}
                  />
                  <div>
                    <p className="font-mono text-[12px] text-ink">{srgb.hex}</p>
                    {srgb.outOfGamut && (
                      <p className="text-[11px] text-caution">Outside sRGB</p>
                    )}
                  </div>
                </div>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
            </td>
          )
        })}
      </tr>

      <ComparisonRow
        label="L* (lightness)"
        values={labs.map((lab) => (lab ? lab.L.toFixed(2) : null))}
      />
      <ComparisonRow label="a* (green–red)" values={labs.map((lab) => (lab ? lab.a.toFixed(2) : null))} />
      <ComparisonRow label="b* (blue–yellow)" values={labs.map((lab) => (lab ? lab.b.toFixed(2) : null))} />

      <tr className="border-b border-line last:border-b-0">
        <th className="px-4 py-3 text-left align-top text-[13px] font-medium text-ink-muted">
          ΔE2000 vs {records[0]?.code}
        </th>
        {records.map((record, index) => {
          const lab = labs[index]
          if (index === 0) {
            return (
              <td key={record.id} className="border-l border-line px-4 py-3 text-ink-muted">
                Reference column
              </td>
            )
          }
          if (!lab || !reference) {
            return (
              <td key={record.id} className="border-l border-line px-4 py-3 text-ink-faint">
                No measurement
              </td>
            )
          }
          const delta = deltaE2000(lab, reference)
          return (
            <td key={record.id} className="border-l border-line px-4 py-3">
              <span className="font-mono text-[15px] text-ink">{delta.toFixed(2)}</span>
              <span className="block text-[12px] text-ink-muted">{describeDeltaE(delta)}</span>
            </td>
          )
        })}
      </tr>
    </tbody>
  )
}

/** Every wizard field that at least one record fills in, grouped by step. */
function DetailsBody({ records }: { records: DetailRecord[] }) {
  const steps = EXPERIMENT_STEPS.filter((step) => step.schema !== null)

  return (
    <>
      {steps.map((step) => {
        const fields = STEP_FIELDS[step.key as keyof typeof STEP_FIELDS].filter((field: FieldDef) =>
          records.some((record) => hasValue(resolve(field, record))),
        )
        if (fields.length === 0) return null

        return (
          <tbody key={step.key}>
            <tr className="border-b border-line bg-paper-muted/60">
              <th
                colSpan={records.length + 1}
                className="px-4 py-2 text-left font-display text-[13px] font-semibold text-ink"
              >
                {step.title}
              </th>
            </tr>
            {fields.map((field) => (
              <ComparisonRow
                key={field.name}
                label={field.label}
                values={records.map((record) => resolve(field, record))}
              />
            ))}
          </tbody>
        )
      })}
    </>
  )
}

/**
 * One labelled row. When the columns disagree the row is flagged, which is the
 * whole point of putting records next to each other.
 */
function ComparisonRow({ label, values }: { label: string; values: (string | null)[] }) {
  const filled = values.filter(hasValue)
  const differs = filled.length > 1 && new Set(filled).size > 1

  return (
    <tr className="border-b border-line last:border-b-0">
      <th className="px-4 py-2.5 text-left align-top text-[13px] font-medium text-ink-muted">
        <span className="flex items-center gap-2">
          {label}
          {differs && (
            <Badge tone="caution" className="shrink-0">
              differs
            </Badge>
          )}
        </span>
      </th>
      {values.map((value, index) => (
        <td
          key={index}
          className={cn(
            'border-l border-line px-4 py-2.5 align-top text-[13px]',
            hasValue(value) ? 'text-ink' : 'text-ink-faint',
          )}
        >
          {hasValue(value) ? value : '—'}
        </td>
      ))}
    </tr>
  )
}
